import { Reveal, SectionLabel, CountUp } from "../Reveal";
import { SPORTS, MAP_LOCATIONS } from "../../data/content";
import { Flame, GraduationCap } from "lucide-react";
import { Countdown } from "../Countdown";

const RACE_COLORS = { spartan: "#BAF173", hyrox: "#6735FA", volunteer: "#11E5B4" };

// Derive a year-descending race timeline from the same map data
const RACE_HISTORY = [...MAP_LOCATIONS]
  .filter((l) => l.type !== "lived")
  .map((l) => ({ ...l, year: (l.note.match(/\d{4}/) || ["—"])[0] }))
  .sort((a, b) => new Date(b.date) - new Date(a.date));

// Hero shot from every pin that has photos (user's pic is always first in each array)
const GALLERY = [...MAP_LOCATIONS]
  .filter((l) => l.photos && l.photos.length)
  .map((l) => ({ src: l.photos[0], photos: l.photos, title: l.title, name: l.name, year: (l.note.match(/\d{4}/) || ["—"])[0], date: l.date, zoom: l.heroZoom, focus: l.heroFocus }))
  .sort((a, b) => new Date(b.date) - new Date(a.date));

export const Sports = () => (
  <section id="sports" className="relative py-24 sm:py-32 md:py-40 bg-[#050505] bg-noise border-t border-white/10" data-testid="sports-section">
    <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
      <SectionLabel color="#11E5B4">{SPORTS.label}</SectionLabel>
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
        <Reveal>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-white leading-[0.95]">
            {SPORTS.heading}
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="flex flex-wrap gap-2">
            {SPORTS.disciplines.map((d) => (
              <span key={d} className="font-display text-xs uppercase tracking-widest border border-white/15 px-4 py-2 text-white/70">{d}</span>
            ))}
          </div>
        </Reveal>
      </div>

      {/* stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-8">
        {SPORTS.stats.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.08}>
            <div className="bg-black p-7 md:p-9 h-full transition-transform duration-300 hover:-translate-y-1">
              <p className="font-display text-5xl md:text-6xl font-bold text-[#11E5B4] leading-none"><CountUp value={s.value} /></p>
              <p className="mt-3 font-display text-xs md:text-sm uppercase tracking-widest text-white/50">{s.label}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.1} className="mb-12">
        <Countdown races={SPORTS.nextRaces} interval={10000} />
      </Reveal>

      {/* gallery + upcoming */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <Reveal delay={0.1}>
            <div className="border border-white/10 bg-[#0A0A0A] p-7 h-full">
              <div className="flex items-center gap-3 mb-6">
                <Flame size={20} className="text-[#BAF173]" />
                <h3 className="font-display text-xl font-semibold uppercase tracking-wide text-white">Race Calendar 2026</h3>
              </div>
              <ul className="space-y-4">
                {SPORTS.upcoming.map((u) => (
                  <li key={u.event} className="flex items-start justify-between gap-4 border-b border-white/10 pb-4 last:border-0" data-testid="upcoming-race">
                    <span className="text-sm text-white/75 leading-snug">{u.event}</span>
                    <span className="font-display text-[10px] uppercase tracking-widest text-black bg-[#BAF173] px-2.5 py-1 shrink-0">{u.tag}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
        <div>
          <Reveal delay={0.15}>
            <div className="border border-white/10 bg-[#0A0A0A] p-7 h-full">
              <div className="flex items-center gap-3 mb-6">
                <GraduationCap size={20} className="text-[#22AED4]" />
                <h3 className="font-display text-xl font-semibold uppercase tracking-wide text-white">Industry &amp; Professional</h3>
              </div>
              <ul className="space-y-4">
                {SPORTS.professional.map((u) => (
                  <li key={u.event} className="flex items-start justify-between gap-4 border-b border-white/10 pb-4 last:border-0" data-testid="professional-item">
                    <span className="text-sm text-white/75 leading-snug">{u.event}</span>
                    <span className="font-display text-[10px] uppercase tracking-widest text-black bg-[#22AED4] px-2.5 py-1 shrink-0">{u.tag}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </div>

      {/* Race history timeline (synced with the world map) */}
      <div className="mt-20">
        <div className="flex items-end justify-between gap-6 mb-8">
          <Reveal>
            <h3 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight text-white">Race History</h3>
          </Reveal>
          <Reveal delay={0.1}>
            <span className="font-display text-xs uppercase tracking-widest text-white/40">{RACE_HISTORY.length} events · 2016 → 2026</span>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {RACE_HISTORY.map((r, i) => (
            <Reveal key={r.id} delay={Math.min(i * 0.03, 0.3)}>
              <button
                onClick={() => {
                  document.getElementById("map")?.scrollIntoView({ behavior: "smooth" });
                  setTimeout(() => window.dispatchEvent(new CustomEvent("reycro:flyto", { detail: r.id })), 700);
                }}
                className="w-full text-left bg-black p-5 h-full flex items-start gap-4 group hover:bg-[#0A0A0A] transition-colors cursor-pointer"
                data-testid="race-history-item"
                title="View on map"
              >
                <span className="font-display text-2xl font-bold leading-none w-14 shrink-0" style={{ color: RACE_COLORS[r.type] }}>{r.year}</span>
                <div className="min-w-0">
                  <p className="font-display text-sm uppercase tracking-wide text-white leading-tight group-hover:text-[#11E5B4] transition-colors">{r.title}</p>
                  <p className="text-xs text-white/45 mt-1 truncate">{r.name}</p>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      {/* Race photo gallery (real event photos) */}
      <div className="mt-20">
        <div className="flex items-end justify-between gap-6 mb-8">
          <Reveal>
            <h3 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight text-white">Race Gallery</h3>
          </Reveal>
          <Reveal delay={0.1}>
            <span className="font-display text-xs uppercase tracking-widest text-white/40">{GALLERY.length} events · tap to view</span>
          </Reveal>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="race-gallery">
          {GALLERY.map((g, i) => (
            <Reveal key={g.src} delay={Math.min(i * 0.04, 0.4)}>
              <button
                onClick={() => window.dispatchEvent(new CustomEvent("reycro:lightbox", { detail: { photos: g.photos, index: 0 } }))}
                className="group relative w-full aspect-[4/5] overflow-hidden border border-white/10 bg-black cursor-pointer shadow-[0_10px_30px_rgba(0,0,0,0.5)] transition-all duration-500 hover:shadow-[0_30px_70px_rgba(0,0,0,0.85)] hover:border-[#11E5B4]/40 hover:-translate-y-1"
                data-testid="gallery-item"
                title={`${g.title} · ${g.year}`}
              >
                <img
                  src={g.src}
                  alt={`${g.title} ${g.year}`}
                  loading="lazy"
                  style={{ objectPosition: g.focus || "center", transform: g.zoom ? `scale(${g.zoom})` : undefined }}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                {/* depth vignette */}
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_35%,rgba(0,0,0,0.55)_100%)]" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/15 to-transparent opacity-95 group-hover:opacity-100 transition-opacity" />
                <div className="absolute bottom-0 left-0 right-0 p-3 text-left">
                  <p className="font-display text-xs uppercase tracking-wide text-white leading-tight">{g.title}</p>
                  <p className="font-display text-[10px] uppercase tracking-widest text-[#11E5B4] mt-0.5">{g.year} · {g.name.split("·")[0].trim()}</p>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);
